import { Alert, Form, Input, Row, Select } from "antd"
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SystemDescriptions } from "src/common/descriptions";
import { CashboxCategory } from "src/common/models/cashboxCategory";
import { ExpenditureRecord } from "src/common/models/expenditureRecord";
import { ServiceCategory } from "src/common/models/serviceCategory";
import { amountValidator, formatToCurrency, getInputRequiredRule } from "src/common/util";
import { Col24, Col6 } from "src/components/Columns";
import { CurrencyInput } from "src/components/CurrencyInput";
import { FormActionButtons } from "src/components/FormActionButtons";
import { AccountPayablesError } from "src/features/AccountPayable/state/types";
import { getCashboxCategoriesApi } from "src/features/CashboxStatement/state/action";
import { getCashboxStmtRecordsApi } from "src/features/CustomerPayment/state/action";
import { getServiceCategoriesApi } from "src/features/Service/state/action";
import { RootState } from "src/state/reducer";

interface ReduxProps {
    error?: AccountPayablesError
    cashboxCategories: CashboxCategory[]
    cashboxBalance: number
    serviceCategories: ServiceCategory[]
}

interface Props {
    initialValues?: ExpenditureRecord
    onFinish: (expenditureRecord: Partial<ExpenditureRecord>) => void
    onCancel: () => void
}

export const ExpenditureRecordForm = (props: Props) => {
    const dispatch = useDispatch()

    const [selectedOrigin, setSelectedOrigin] = useState()

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state.accountPayables.error,
        cashboxCategories: state.cashbox.cashboxCategories || [],
        cashboxBalance: state.contractPayment.cashboxBalance || 0,
        serviceCategories: state.service.serviceCategories || [],
    }))

    useEffect(() => {
        dispatch(getCashboxCategoriesApi({
            type: "O",
        }))
        dispatch(getCashboxStmtRecordsApi({
            filter: "CASHBOX_BALANCE",
        }))
        dispatch(getServiceCategoriesApi())
    }, [])

    const descriptions = SystemDescriptions.PAGES.ACCOUNT_PAYABLES.CREATE_EXPENDITURE_RECORD
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const [form] = Form.useForm();

    const onFinish = (formValues) => {
        const expenditureRecord: ExpenditureRecord = {
            datetime: '',
            description: formValues.description,
            total: Number(formValues.total),
            cashboxCategoryId: formValues.category,
            userId: 0,
            isDeleted: false,
            origin: formValues.origin,
            serviceCategoryId: formValues.serviceCategoryId,
        }

        props.onFinish(expenditureRecord)
    }

    const getButtonStatus = (): boolean => {
        return props.initialValues
            ? false
            : !form.isFieldsTouched(true) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0
    }

    const handleCancelClick = () => {
        props.onCancel()
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CANCEL_BUTTON}
            actionText={descriptions.SAVE_BUTTON}
            actionDisabled={getButtonStatus}
            onCancel={handleCancelClick}
        />
    )

    const handleOriginChange = (e) => {
        setSelectedOrigin(e)
    }

    const renderForm = () => (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
        >
            <Row gutter={[16, 8]}>
                <Col24>
                    <Form.Item
                        label={descriptions.FORM.DESCRIPTION.LABEL}
                        name={"description"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.DESCRIPTION.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.DESCRIPTION.PLACEHOLDER}
                            maxLength={45}
                            showCount
                        />
                    </Form.Item>
                </Col24>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.SERVICE_CATEGORY.LABEL}
                        name={"serviceCategoryId"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.SERVICE_CATEGORY.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Select
                            options={reduxProps.serviceCategories.map(serviceCategory => ({
                                value: serviceCategory.id,
                                label: serviceCategory.name,
                            }))}
                            placeholder={descriptions.FORM.SERVICE_CATEGORY.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.CATEGORY.LABEL}
                        name={"category"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.CATEGORY.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Select
                            options={reduxProps.cashboxCategories.map(category => ({
                                value: category.id,
                                label: category.name,
                            }))}
                            placeholder={descriptions.FORM.CATEGORY.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={`${descriptions.FORM.ORIGIN.LABEL} ${selectedOrigin === "C" ? `${descriptions.BALANCE} ${formatToCurrency(reduxProps.cashboxBalance.toString())}` : ''}`}
                        name={"origin"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.ORIGIN.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Select
                            options={[
                                {
                                    label: descriptions.CASHBOX,
                                    value: "C",
                                },
                                {
                                    label: descriptions.BANK,
                                    value: "B",
                                },
                            ]}
                            placeholder={descriptions.FORM.ORIGIN.PLACEHOLDER}
                            onChange={handleOriginChange}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.TOTAL.LABEL}
                        name={"total"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.TOTAL.LABEL),
                            {
                                validator: (_, value) => {
                                    return amountValidator(value)
                                }
                            },
                            {
                                validator: (_, value) => {
                                    const { origin } = form.getFieldsValue()
                                    console.log("ORIGIN", origin)

                                    if (origin === "C" && value > reduxProps.cashboxBalance) {
                                        return Promise.reject(descriptions.AMOUNT_MUST_BE_GT_BALANCE)
                                    }

                                    return Promise.resolve()
                                }
                            },
                        ]}
                        validateTrigger={["onBlur", "onChange"]}
                    >
                        <CurrencyInput
                            placeholder={descriptions.FORM.TOTAL.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
            </Row>
            {
                reduxProps.error &&
                <Alert
                    message={"ERROR"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {renderButtons()}
        </Form>
    )

    return (
        renderForm()
    )
}
